import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { formatFormDisplayName } from '../../utils/formName';
import './RecentlyDeleted.css';

const RecentlyDeleted = () => {
  const { user } = useAuth();
  const [deletedForms, setDeletedForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [restoreConfirm, setRestoreConfirm] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => {
    console.log('🔄 RecentlyDeleted component mounted, fetching data...');
    fetchDeletedForms();
  }, []); // Empty dependency array means this runs once on mount

  const fetchDeletedForms = async () => {
    try {
      console.log('🔄 Fetching deleted forms for parent...');
      console.log('📡 API Call: GET /api/form-submissions/my-deleted-forms');
      const response = await axios.get('/api/form-submissions/my-deleted-forms');
      console.log('✅ Deleted forms response status:', response.status);
      console.log('📦 Raw response data:', response.data);
      console.log('🔢 Number of deleted items:', response.data?.length || 0);

      if (response.data && Array.isArray(response.data)) {
        response.data.forEach((item, index) => {
          console.log(`  📄 Item ${index + 1}: ${item.student_first_name} ${item.student_last_name} (${item.type}) - deleted: ${item.deleted_at}`);
        });
      } else {
        console.log('⚠️ Response data is not an array:', typeof response.data);
      }

      setDeletedForms(response.data || []);
    } catch (error) {
      console.error('❌ Failed to fetch deleted forms:', error);
      console.error('❌ Error response data:', error.response?.data);
      console.error('❌ Error status:', error.response?.status);
      console.error('❌ Error message:', error.message);
      alert(`❌ Failed to load deleted forms: ${error.response?.data?.error || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async (item) => {
    try {
      if (item.type === 'student') {
        await axios.post(`/api/form-submissions/restore-student/${item.id}`);
        alert('Student restored successfully');
      } else if (item.type === 'student_intake') {
        await axios.post(`/api/form-submissions/restore-student-intake/${item.id}`);
        alert('Student intake restored successfully');
      } else {
        await axios.post(`/api/form-submissions/restore-form/${item.id}`);
        alert('Form restored successfully');
      }
      setDeletedForms(deletedForms.filter(f => f.id !== item.id));
      setRestoreConfirm(null);
    } catch (error) {
      console.error('Failed to restore:', error);
      if (error.response?.status === 404) {
        // Record was already restored or deleted, remove from UI anyway
        alert('Record was already restored or no longer exists');
        setDeletedForms(deletedForms.filter(f => f.id !== item.id));
        setRestoreConfirm(null);
      } else {
        alert(`Failed to restore: ${error.response?.data?.error || error.message}`);
      }
    }
  };

  const handlePermanentDelete = async (item) => {
    try {
      if (item.type === 'student') {
        await axios.delete(`/api/form-submissions/my-permanent-delete-student/${item.id}`);
        alert('Student permanently deleted');
      } else if (item.type === 'student_intake') {
        await axios.delete(`/api/form-submissions/my-permanent-delete-student-intake/${item.id}`);
        alert('Student intake permanently deleted');
      } else {
        await axios.delete(`/api/form-submissions/my-permanent-delete/${item.id}`);
        alert('Form permanently deleted');
      }
      setDeletedForms(deletedForms.filter(f => f.id !== item.id));
      setDeleteConfirm(null);
    } catch (error) {
      console.error('Failed to permanently delete:', error);
      if (error.response?.status === 404) {
        // Record was already deleted, remove from UI anyway
        alert('Record was already permanently deleted');
        setDeletedForms(deletedForms.filter(f => f.id !== item.id));
        setDeleteConfirm(null);
      } else {
        alert(`Failed to permanently delete: ${error.response?.data?.error || error.message}`);
      }
    }
  };

  if (loading) {
    return <div className="loading">Loading deleted forms...</div>;
  }

  return (
    <div className="recently-deleted">
      <div className="page-header">
        <h1>Recently Deleted</h1>
        <p>Forms and students that have been deleted in the last 7 days. They will be permanently deleted after 7 days.</p>
      </div>

      {restoreConfirm && (
        <div className="modal-overlay" onClick={() => setRestoreConfirm(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>Restore {restoreConfirm.type === 'student' ? 'Student' : 'Form'}</h3>
            <p>Are you sure you want to restore this {restoreConfirm.type === 'student' ? 'student' : 'form'}? It will be available again in your {restoreConfirm.type === 'student' ? 'students' : 'forms'} list.</p>
            <div className="modal-actions">
              <button onClick={() => handleRestore(restoreConfirm)} className="btn-primary">Restore</button>
              <button onClick={() => setRestoreConfirm(null)} className="btn-secondary">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {deleteConfirm && (
        <div className="modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>{deleteConfirm.type === 'student_intake' ? 'Remove' : 'Permanently Delete'} {deleteConfirm.type === 'student' ? 'Student' : deleteConfirm.type === 'student_intake' ? 'Student Intake' : 'Form'}</h3>
            <p><strong>Warning:</strong> This action cannot be undone. The {deleteConfirm.type === 'student' ? 'student' : deleteConfirm.type === 'student_intake' ? 'student intake record' : 'form'} will be {deleteConfirm.type === 'student_intake' ? 'permanently removed from the recently deleted list' : 'permanently deleted from the database'}.</p>
            <div className="modal-actions">
              <button onClick={() => handlePermanentDelete(deleteConfirm)} className="btn-danger">Permanently Delete</button>
              <button onClick={() => setDeleteConfirm(null)} className="btn-secondary">Cancel</button>
            </div>
          </div>
        </div>
      )}

      <div className="deleted-forms-list">
        {deletedForms.length === 0 ? (
          <div className="no-deleted-forms">
            <p>No recently deleted forms found.</p>
          </div>
        ) : (
          <div className="forms-grid">
            {deletedForms.map(item => (
              <div key={item.id} className="form-card deleted">
                <div className="form-header">
                  <h3>{item.type === 'student' ? 'Student Record' : item.type === 'student_intake' ? 'Student Intake Record' : formatFormDisplayName(item.instance_name || item.form_name, { includeYear: false })}</h3>
                  <span className="student-name">{item.student_first_name} {item.student_last_name}</span>
                  {item.type === 'student' && item.grade && (
                    <span className="grade-badge">Grade {item.grade}</span>
                  )}
                </div>
                <div className="form-meta">
                  <p><strong>Deleted:</strong> {new Date(item.deleted_at).toLocaleDateString()}</p>
                  <p><strong>Created:</strong> {new Date(item.created_at).toLocaleDateString()}</p>
                  {item.type !== 'student' && (
                    <p><strong>Form Data:</strong> {item.form_data ? Object.keys(item.form_data).length : 0} fields</p>
                  )}
                  {item.type === 'student' && (
                    <p><strong>Type:</strong> Student Record</p>
                  )}
                  {item.type === 'student_intake' && (
                    <p><strong>Type:</strong> Student Intake Record</p>
                  )}
                </div>
                <div className="form-actions">
                  <button
                    onClick={() => setRestoreConfirm(item)}
                    className="btn-primary"
                  >
                    Restore {item.type === 'student' ? 'Student' : item.type === 'student_intake' ? 'Student Intake' : 'Form'}
                  </button>
                  <button
                    onClick={() => setDeleteConfirm(item)}
                    className="btn-danger"
                  >
                    Delete Forever
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default RecentlyDeleted;
